import React, { useEffect, useState, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { FaBox, FaToggleOn, FaToggleOff, FaMortarPestle } from 'react-icons/fa';
import { MdFastfood } from 'react-icons/md';
import { Option, IngredientSelection } from '../../../../../../../../types';
import { ItemStock } from '../../../../../../../../../../types';
import IngredientsManager from './components/IngredientsManager/IngredientsManager';
import UnitsConfiguration from './components/UnitsConfiguration/UnitsConfiguration';
import CurrentStock from './components/CurrentStock/CurrentStock';
import './StockConfiguration.scss';


interface StockConfigurationProps {
  // Stock toggle
  isStock: boolean;
  setIsStock: (val: boolean) => void;
  
  // Stock management mode
  stockManagementMode: 'item' | 'ingredients' | null;
  setStockManagementMode: (mode: 'item' | 'ingredients' | null) => void;
  
  // Units and stock data
  unidadesMedida: Option[];
  setUnidadMedidaId?: (val: number) => void;
  unidadMedidaId?: number;
  selectedUnidadName: string;
  setSelectedUnidadName?: (val: string) => void;
  currentStock: string;
  setCurrentStock: (value: string) => void;
  minQuantity: string;
  setMinQuantity: (value: string) => void;
  
  // Storage units
  storageUnits: any[];
  selectedStorageUnit: any;
  setSelectedStorageUnit: (unit: any) => void;

  // Ingredients management
  ingredientSelections?: IngredientSelection[];
  setIngredientSelections?: React.Dispatch<React.SetStateAction<IngredientSelection[]>>;
  
  // NEW: Combo group management
  isComboGroup?: boolean;
  setIsComboGroup?: (val: boolean) => void;
  
  // NEW: Update mode detection
  currentItemId?: number | string;
  wasStockControlEnabled?: boolean; // Whether stock control was enabled when the modal opened
  
  // NEW: Item stocks for display
  itemStocks?: ItemStock[];
  setItemStocks?: (stocks: ItemStock[]) => void;
  
  // NEW: Local ID for feature flags
  localId?: number;

  // NEW: Callback to notify parent of validation state
  onValidationChange?: (isValid: boolean) => void;
}

const StockConfiguration: React.FC<StockConfigurationProps> = ({
  isStock,
  setIsStock,
  stockManagementMode,
  setStockManagementMode,
  unidadesMedida,
  setUnidadMedidaId = () => {},
  unidadMedidaId = 0,
  selectedUnidadName,
  setSelectedUnidadName = () => {},
  currentStock,
  setCurrentStock,
  minQuantity,
  setMinQuantity,
  storageUnits,
  selectedStorageUnit,
  setSelectedStorageUnit,
  ingredientSelections = [],
  setIngredientSelections = () => {},
  isComboGroup = false, // NEW: default value for is_combo_group
  setIsComboGroup = () => {}, // NEW: default setter for is_combo_group
  currentItemId, // NEW: for update mode detection
  wasStockControlEnabled = false, // NEW: whether stock was enabled when modal opened
  itemStocks = [], // NEW: for displaying item stocks
  setItemStocks = () => {}, // NEW: for updating item stocks
  localId = 1,
  onValidationChange = () => {},
}) => {
  // Validation states
  const [unidadError, setUnidadError] = useState(false);
  const [stockError, setStockError] = useState(false);
  const [minimoError, setMinimoError] = useState(false);
  const [almacenamientoError, setAlmacenamientoError] = useState(false);

  // Track which fields have been touched
  const [touchedFields, setTouchedFields] = useState<{ unidad?: boolean; stock?: boolean; minimo?: boolean; almacenamiento?: boolean }>({});

  const validateFields = useCallback(() => {
    let isValid = true;
    const isEditingExistingItemStock = Boolean(
      currentItemId &&
      wasStockControlEnabled &&
      isStock &&
      stockManagementMode === 'item' &&
      !isComboGroup
    );

    // In update mode, when stock was already enabled, unit/stock/min/storage fields are not editable here
    // (inventory is managed by storage cards), so this section should not block form submission.
    if (isEditingExistingItemStock) {
      setUnidadError(false);
      setStockError(false);
      setMinimoError(false);
      setAlmacenamientoError(false);
      onValidationChange?.(true);
      return true;
    }

    // Only validate if stock is enabled and in item mode and not combo group
    if (isStock && stockManagementMode === 'item' && !isComboGroup) {
      const unidadValid = unidadMedidaId > 0;
      const stockValid = currentStock.trim() !== '';
      const minimoValid = minQuantity.trim() !== '';
      const almacenamientoValid = selectedStorageUnit !== null && selectedStorageUnit !== undefined && selectedStorageUnit !== '';

      // For unidad: NEVER show error if it has a valid value (even default)
      // For other fields: only show error after they've been touched
      setUnidadError(false); // Don't show error for unidad since it has a default value
      setStockError(!stockValid && !!touchedFields.stock);
      setMinimoError(!minimoValid && !!touchedFields.minimo);
      setAlmacenamientoError(!almacenamientoValid && !!touchedFields.almacenamiento);

      isValid = unidadValid && stockValid && minimoValid && almacenamientoValid;
    } else {
      // Clear all errors when not in item mode
      setUnidadError(false);
      setStockError(false);
      setMinimoError(false);
      setAlmacenamientoError(false);
    }

    onValidationChange?.(isValid);
    return isValid;
  }, [isStock, stockManagementMode, isComboGroup, unidadMedidaId, selectedUnidadName, currentStock, minQuantity, selectedStorageUnit, touchedFields, onValidationChange, currentItemId, wasStockControlEnabled]);

  // Re-validate when relevant values change
  useEffect(() => {
    validateFields();
  }, [isStock, stockManagementMode, isComboGroup, unidadMedidaId, selectedUnidadName, currentStock, minQuantity, selectedStorageUnit, validateFields]);

  const handleFieldBlur = (field: 'unidad' | 'stock' | 'minimo' | 'almacenamiento') => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    validateFields();
  };
  // Set default unit when component mounts
  useEffect(() => {
    const defaultUnidad = unidadesMedida.find((um) => um.id === 5);
    if (defaultUnidad && setUnidadMedidaId && setSelectedUnidadName) {
      setUnidadMedidaId(defaultUnidad.id);
      setSelectedUnidadName(defaultUnidad.name);
      // Mark as touched since we're setting a default value
      setTouchedFields(prev => ({ ...prev, unidad: true }));
    }
  }, [unidadesMedida, setUnidadMedidaId, setSelectedUnidadName]);

  const handleUnidadMedidaSelect = (id: number, name: string) => {
    setUnidadMedidaId(id);
    setSelectedUnidadName(name);
    setTouchedFields(prev => ({ ...prev, unidad: true }));
    validateFields();
  };

  const handleStockToggle = (val: boolean) => {
    setIsStock(val);
    if (val) {
      setStockManagementMode(stockManagementMode ?? 'item');
    } else {
      setStockManagementMode(null);
    }
  };

  const handleStockModeChange = (mode: 'item' | 'ingredients') => {
    setStockManagementMode(mode);
    if (mode === 'item') {
      setIngredientSelections([]);
    }
  };

  return (
    <div className="stock-configuration">
      <div className="config-header">
        <h6 className="config-title">Configuración de Stock</h6>
        <p className="config-subtitle">Define el control de inventario y unidades del producto</p>
      </div>

      <div className="config-content">
        {/* Stock Toggle */}
        <div className="config-section">
          <div className="config-item">
            <div className="config-label">
              <FaBox className="config-icon" />
              <div className="label-content">
                <span className="label-title">Control de inventario</span>
                <span className="label-subtitle">Controlar stock automáticamente</span>
              </div>
            </div>
            <div className="config-toggle" onClick={() => handleStockToggle(!isStock)}>
              {isStock ? (
                <FaToggleOn className="toggle-icon active" />
              ) : (
                <FaToggleOff className="toggle-icon inactive" />
              )}
              <span className={`toggle-status ${isStock ? 'active' : 'inactive'}`}>
                {isStock ? 'Activado' : 'Desactivado'}
              </span>
            </div>
          </div>
        </div>

        {/* Stock Management Mode Selection */}
        {isStock && (
          <div className="stock-management-question">
            <div className="question-header">
              <div className="mps-divider-items-modal" />
              <label className="mps-item-modal-label">¿Cómo deseas gestionar el stock?</label>
            </div>
            <div className="options">
              {/* 
                Disable 'item' mode when editing an existing item that was configured with ingredient-based stock.
                This prevents switching back to item-based stock which would cause backend complications.
              */}
              {(() => {
                const isExistingIngredientStock = !!(currentItemId && wasStockControlEnabled && stockManagementMode === 'ingredients');
                return (
                  <>
                    <div
                      className={`stock-mode-card ${stockManagementMode === 'item' ? 'selected' : ''} ${isExistingIngredientStock ? 'disabled' : ''}`}
                      onClick={() => !isExistingIngredientStock && handleStockModeChange('item')}
                      role="button"
                      tabIndex={isExistingIngredientStock ? -1 : 0}
                      title={isExistingIngredientStock ? 'No es posible cambiar a stock por ítem cuando ya está configurado por ingredientes' : undefined}
                    >
                      <div className="icon"><FaBox /></div>
                      <div className="text">
                        <div className="title">Con el ítem</div>
                        <div className="subtitle">Stock directo del producto</div>
                      </div>
                      <Form.Check
                        type="radio"
                        id="stock-mode-item"
                        name="stock-management-mode"
                        className="visually-hidden-radio"
                        checked={stockManagementMode === 'item'}
                        onChange={() => {}}
                        disabled={isExistingIngredientStock}
                      />
                    </div>

                    <div
                      className={`stock-mode-card ${stockManagementMode === 'ingredients' ? 'selected' : ''}`}
                      onClick={() => handleStockModeChange('ingredients')}
                      role="button"
                      //tabIndex={localId !== 1 ? -1 : 0}
                      //title={localId !== 1 ? 'Esta funcionalidad estará disponible próximamente' : undefined}
                    >
                      <div className="icon"><FaMortarPestle /></div>
                      <div className="text">
                        <div className="title">Con los ingredientes</div>
                        <div className="subtitle">El stock vive en los insumos</div>
                      </div>
                      {/**localId !== 1 && (
                        <div className="coming-soon-badge">Próximamente</div>
                      )**/}
                      <Form.Check
                        type="radio"
                        id="stock-mode-ingredients"
                        name="stock-management-mode"
                        className="visually-hidden-radio"
                        checked={stockManagementMode === 'ingredients'}
                        onChange={() => {}}
                        //disabled={localId !== 1}
                      />
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Combo Group Configuration (only for item-based stock) */}
        {isStock && stockManagementMode === 'item' && (
          <div className="config-section combo-group-section">
            <div className="config-item">
              <div className="config-label">
                <MdFastfood className="config-icon" />
                <div className="label-content">
                  <span className="label-title">Grupo de productos</span>
                  <span className="label-subtitle">Este ítem es un conjunto de productos</span>
                </div>
              </div>
              <div className="config-toggle" onClick={() => setIsComboGroup(!isComboGroup)}>
                {isComboGroup ? (
                  <FaToggleOn className="toggle-icon active" />
                ) : (
                  <FaToggleOff className="toggle-icon inactive" />
                )}
                <span className={`toggle-status ${isComboGroup ? 'active' : 'inactive'}`}>
                  {isComboGroup ? 'Activado' : 'Desactivado'}
                </span>
              </div>
            </div>
            
            {/* Explanatory banner */}
            {isComboGroup && (
              <div className="stock-config-combo-group-info-banner">
                <div className="stock-config-banner-content">
                  <div className="stock-config-banner-icon">
                    <MdFastfood />
                  </div>
                  <div className="stock-config-banner-text">
                    <h4>¿Qué significa "Grupo de productos"?</h4>
                    <p>
                      Al activar esta opción, estás indicando que este ítem <strong>no es un producto individual</strong>, 
                      sino un <strong>conjunto o combo de varios productos</strong>. Por ejemplo: una "Combo Hamburguesa" 
                      que incluye hamburguesa + papas + bebida.
                    </p>
                    <p>
                      <strong>Importante:</strong> No necesitas configurar el stock de este ítem porque el inventario 
                      se manejará automáticamente a través de los productos individuales que forman parte del combo.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Units Configuration (only for item-based stock and NOT combo group) */}
        {/* Show UnitsConfiguration only if:
            1. In create mode (no currentItemId), OR
            2. In update mode AND stock control is being enabled for the first time (!wasStockControlEnabled)
        */}
        {isStock && stockManagementMode === 'item' && !isComboGroup && (!currentItemId || !wasStockControlEnabled) && (
          <UnitsConfiguration
            unidadesMedida={unidadesMedida}
            selectedUnidadName={selectedUnidadName}
            onUnidadMedidaSelect={handleUnidadMedidaSelect}
            currentStock={currentStock}
            setCurrentStock={setCurrentStock}
            minQuantity={minQuantity}
            setMinQuantity={setMinQuantity}
            storageUnits={storageUnits}
            selectedStorageUnit={selectedStorageUnit}
            setSelectedStorageUnit={setSelectedStorageUnit}
            unidadError={unidadError}
            stockError={stockError}
            minimoError={minimoError}
            almacenamientoError={almacenamientoError}
            onUnidadBlur={() => handleFieldBlur('unidad')}
            onStockBlur={() => handleFieldBlur('stock')}
            onMinimoBlur={() => handleFieldBlur('minimo')}
            onAlmacenamientoBlur={() => handleFieldBlur('almacenamiento')}
          />
        )}

        {/* Current Stock Display (only in update mode when stock control is already enabled) */}
        {isStock && stockManagementMode === 'item' && !isComboGroup && currentItemId && wasStockControlEnabled && itemStocks.length > 0 && (
          <CurrentStock
            itemId={currentItemId}
            itemStocks={itemStocks}
            setItemStocks={setItemStocks}
            unidadesMedida={unidadesMedida}
          />
        )}

        {/* Ingredients Manager (only for ingredient-based stock) */}
        {isStock && stockManagementMode === 'ingredients' && (
          <IngredientsManager
            ingredientSelections={ingredientSelections}
            setIngredientSelections={setIngredientSelections}
          />
        )}
      </div>
    </div>
  );
};

export default StockConfiguration;
